import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  ReactNode
} from 'react'
import { GlobalStyles, alpha } from '@mui/material'
import { useUIMode } from './UIContext'
import { playScanBeep, unlockScanBeep } from '../utils/scanBeep'

/**
 * Lleva lo que teclea el lector fisico (HID) al campo de escaneo activo en
 * mobile, SIN que ese campo tenga el foco.
 *
 * ── El problema ──
 * En mobile los inputs de escaneo no se enfocan solos (ver useScanKeyboard):
 * el foco abre el teclado virtual. Pero sin foco, lo que dispara el lector
 * fisico no entra en ningun campo, y el operario tenia que tocar el input
 * (abriendo el teclado) para poder escanear.
 *
 * ── La solucion ──
 * Un listener global junta las teclas que llegan sin ningun campo enfocado y,
 * con el Enter, le entrega el codigo al "destino de escaneo" activo. Cada
 * CameraScanButton se registra como destino con su `onScan`: es el mismo camino
 * que ya usa la camara (escribir el valor, buscar y avanzar), asi que el lector
 * fisico se comporta igual que la camara sin tocar ninguna pantalla.
 *
 * Gana el ultimo destino registrado: coincide con como montan las pantallas
 * (el paso actual del stepper, la seccion no validada, el modal abierto).
 *
 * Como no hay foco real, el campo del destino activo se resalta con un borde
 * azul difuminado (atributo `data-scan-active`), para que el operario sepa
 * donde va a caer el escaneo.
 *
 * ── Alcance ──
 * Solo mobile, en los dos modos de captura. En desktop el listener no se
 * engancha: ahi los inputs tienen foco y el lector escribe por el camino nativo.
 *
 * Si el operario toca un campo, ese campo queda enfocado y el listener se hace
 * a un lado: el teclado y el Enter del propio input funcionan como siempre.
 */

/** Pausa maxima entre teclas del mismo codigo. Un lector tarda pocos ms. */
const MAX_KEY_GAP_MS = 300

const ACTIVE_ATTR = 'data-scan-active'

interface ScanTarget {
  id: number
  onScanRef: React.MutableRefObject<(value: string) => void>
  disabledRef: React.MutableRefObject<boolean>
  anchorRef: React.RefObject<HTMLElement>
  inputRefRef: React.MutableRefObject<React.RefObject<HTMLInputElement> | undefined>
}

interface ScanTargetContextType {
  register: (target: ScanTarget) => () => void
  refreshHighlight: () => void
}

const ScanTargetContext = createContext<ScanTargetContextType | undefined>(undefined)

const isEditable = (el: Element | null): boolean => {
  if (!el || !(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export const ScanTargetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isMobile, isScanBeepEnabled } = useUIMode()
  const targetsRef = useRef<ScanTarget[]>([])
  const highlightedRef = useRef<HTMLElement | null>(null)

  const isMobileRef = useRef(isMobile)
  isMobileRef.current = isMobile

  // Por ref para no re-enganchar el listener (y perder el buffer) al cambiarlo.
  const isScanBeepEnabledRef = useRef(isScanBeepEnabled)
  isScanBeepEnabledRef.current = isScanBeepEnabled

  /**
   * Pone el resaltado en el campo del destino activo y lo saca del anterior.
   * Se llama despues de cada commit de un destino, al registrar/desregistrar y
   * cuando cambia el foco, asi que siempre refleja el estado actual.
   */
  const refreshHighlight = useCallback(() => {
    const target = targetsRef.current[targetsRef.current.length - 1]
    const input = target?.inputRefRef.current?.current ?? null

    // Con un campo enfocado ya se ve el foco nativo: no se resalta otro.
    const show =
      isMobileRef.current &&
      !!input &&
      !target.disabledRef.current &&
      !input.disabled &&
      !isEditable(document.activeElement)

    // El borde de MUI vive en el root del input, no en el <input>.
    const next = show ? (input!.closest('.MuiInputBase-root') as HTMLElement | null) ?? input : null

    if (highlightedRef.current !== next) {
      highlightedRef.current?.removeAttribute(ACTIVE_ATTR)
      highlightedRef.current = next
    }
    next?.setAttribute(ACTIVE_ATTR, 'true')
  }, [])

  const register = useCallback(
    (target: ScanTarget) => {
      targetsRef.current = [...targetsRef.current, target]
      refreshHighlight()
      return () => {
        targetsRef.current = targetsRef.current.filter((t) => t.id !== target.id)
        refreshHighlight()
      }
    },
    [refreshHighlight]
  )

  useEffect(() => {
    refreshHighlight()
    if (!isMobile) return

    let buffer = ''
    let lastKeyAt = 0

    const handleKeyDown = (e: KeyboardEvent) => {
      // Hay un campo enfocado (el operario lo toco): camino nativo, sin beep.
      if (isEditable(e.target as Element) || isEditable(document.activeElement)) return
      if (e.ctrlKey || e.altKey || e.metaKey) return

      const now = Date.now()
      if (now - lastKeyAt > MAX_KEY_GAP_MS) buffer = ''
      lastKeyAt = now

      if (e.key === 'Enter') {
        const code = buffer.trim()
        buffer = ''
        // El segundo Enter que mandan algunas pistolas llega con buffer vacio.
        if (!code) return

        const target = targetsRef.current[targetsRef.current.length - 1]
        if (!target || target.disabledRef.current) return

        // Con un modal abierto que no contiene al destino (ej: una confirmacion),
        // el escaneo no debe disparar la accion de la pantalla de atras.
        const dialog = document.activeElement?.closest('[role="dialog"]')
        const anchor = target.anchorRef.current
        if (dialog && (!anchor || !dialog.contains(anchor))) return

        e.preventDefault()
        if (isScanBeepEnabledRef.current) playScanBeep()
        target.onScanRef.current(code)
        return
      }

      if (e.key.length === 1) {
        buffer += e.key
        e.preventDefault()
      }
    }

    // focusout se dispara antes de que el foco llegue al siguiente elemento.
    const handleFocusChange = () => setTimeout(refreshHighlight, 0)

    // El navegador no deja sonar audio hasta la primera interaccion: el primer
    // toque deja el contexto listo para el beep.
    window.addEventListener('pointerdown', unlockScanBeep, { passive: true })
    window.addEventListener('keydown', handleKeyDown, true)
    document.addEventListener('focusin', handleFocusChange)
    document.addEventListener('focusout', handleFocusChange)
    return () => {
      window.removeEventListener('pointerdown', unlockScanBeep)
      window.removeEventListener('keydown', handleKeyDown, true)
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('focusout', handleFocusChange)
    }
  }, [isMobile, refreshHighlight])

  const value = useMemo(() => ({ register, refreshHighlight }), [register, refreshHighlight])

  return (
    <ScanTargetContext.Provider value={value}>
      <GlobalStyles
        styles={(theme) => ({
          [`[${ACTIVE_ATTR}="true"]`]: {
            borderRadius: theme.shape.borderRadius,
            boxShadow: `0 0 0 3.5px ${alpha(theme.palette.primary.main, 0.35)}, 0 0 15px 5px ${alpha(theme.palette.primary.main, 0.46)}`,
            transition: 'box-shadow 150ms ease-in-out'
          },
          [`.MuiOutlinedInput-root[${ACTIVE_ATTR}="true"] .MuiOutlinedInput-notchedOutline`]: {
            borderColor: `${theme.palette.primary.main} !important`,
            borderWidth: '3px !important'
          }
        })}
      />
      {children}
    </ScanTargetContext.Provider>
  )
}

let nextTargetId = 1

interface UseScanTargetArgs {
  /** Recibe el codigo leido. Mismo contrato que el `onScan` de la camara. */
  onScan: (value: string) => void
  /** Mientras sea true el destino sigue registrado pero no recibe codigos. */
  disabled?: boolean
  /** false desregistra el destino (por ejemplo un boton `hidden`). */
  enabled?: boolean
  /** Input que llena este destino: es el que se resalta mientras esta activo. */
  inputRef?: React.RefObject<HTMLInputElement>
}

/**
 * Registra al componente como destino del lector fisico en mobile. Devuelve una
 * ref que hay que colgar de un elemento del componente: sirve para saber si el
 * destino vive dentro del modal abierto.
 *
 * Fuera de un ScanTargetProvider no hace nada (por ejemplo en tests).
 */
export const useScanTarget = ({
  onScan,
  disabled = false,
  enabled = true,
  inputRef
}: UseScanTargetArgs) => {
  const context = useContext(ScanTargetContext)
  const anchorRef = useRef<HTMLSpanElement>(null)

  // Van por ref para no re-registrar (y perder el orden) en cada render.
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled
  const inputRefRef = useRef(inputRef)
  inputRefRef.current = inputRef

  const register = context?.register
  const refreshHighlight = context?.refreshHighlight

  useEffect(() => {
    if (!register || !enabled) return
    return register({ id: nextTargetId++, onScanRef, disabledRef, anchorRef, inputRefRef })
  }, [register, enabled])

  // Sin deps a proposito: en cada render puede cambiar el input destino (un
  // mismo boton para posicion y SKU), disabled, o el input puede montarse recien.
  useEffect(() => {
    refreshHighlight?.()
  })

  return anchorRef
}
